import {
  Button,
  Card,
  List,
  Stack,
  Table,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useClientPolicies,
  useClientPolicyDefaults,
} from '@omnipresentgroup/domains';
import {
  ClientPolicyCategory,
  type LeaveTypeEnum,
} from '@omnipresentgroup/types';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';

import {
  EmploymentTimeClientPolicyActionItem,
  type EmploymentTimeClientPolicyItem,
  useClientLeaveSettingTableConfig,
} from '../hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const EMPTY_ARRAY: EmploymentTimeClientPolicyItem[] = [];
const LOADING_ARRAY = Array.from(
  { length: 1 },
  () => ({}) as EmploymentTimeClientPolicyItem,
);

export type LeaveSettingSummaryProps = {
  companyId: string;
  countryCode: string;
  leaveType: LeaveTypeEnum;
};

export const LeaveSettingSummary = ({
  companyId,
  countryCode,
  leaveType,
}: LeaveSettingSummaryProps) => {
  const { data: defaultPolicies, isLoading: isDefaultPoliciesLoading } =
    useClientPolicyDefaults();

  const { data: clientCountryPolicies, isLoading: isClientPoliciesLoading } =
    useClientPolicies({
      clientId: companyId,
      countryCode: countryCode,
      enabled: !!countryCode,
    });

  const clientCountryPolicy = useMemo(
    () => clientCountryPolicies?.[0],
    [clientCountryPolicies],
  );

  const statutoryCountryPolicy = useMemo(
    () => defaultPolicies?.find((p) => p.country.code === countryCode),
    [countryCode, defaultPolicies],
  );

  const { columns } = useClientLeaveSettingTableConfig(leaveType);

  const policySummaryList = useMemo<EmploymentTimeClientPolicyItem[]>(
    () => [
      ...(statutoryCountryPolicy && clientCountryPolicy
        ? [
            {
              policy: statutoryCountryPolicy,
              label: ClientPolicyCategory.STATUTORY,
              value: statutoryCountryPolicy.description,
              isApplied:
                clientCountryPolicy.category === ClientPolicyCategory.STATUTORY,
              action:
                clientCountryPolicy.category === ClientPolicyCategory.CUSTOM
                  ? EmploymentTimeClientPolicyActionItem.APPLY
                  : EmploymentTimeClientPolicyActionItem.NONE,
              statutoryDescription: statutoryCountryPolicy.description,
            },
          ]
        : []),
      ...(clientCountryPolicy &&
      statutoryCountryPolicy &&
      clientCountryPolicy.category === ClientPolicyCategory.CUSTOM
        ? [
            {
              policy: clientCountryPolicy,
              label: ClientPolicyCategory.CUSTOM,
              value: clientCountryPolicy.description,
              isApplied:
                clientCountryPolicy.category === ClientPolicyCategory.CUSTOM,
              action: EmploymentTimeClientPolicyActionItem.EDIT,
              statutoryDescription: statutoryCountryPolicy.description,
            },
          ]
        : []),
      ...(clientCountryPolicy &&
      statutoryCountryPolicy &&
      clientCountryPolicy.customMinimum &&
      clientCountryPolicy.category === ClientPolicyCategory.STATUTORY
        ? [
            {
              policy: clientCountryPolicy,
              label: ClientPolicyCategory.CUSTOM,
              value: '',
              isApplied: false,
              action: EmploymentTimeClientPolicyActionItem.EDIT,
              statutoryDescription: statutoryCountryPolicy.description,
            },
          ]
        : []),
    ],
    [clientCountryPolicy, statutoryCountryPolicy],
  );

  const isLoading = useMemo(
    () => isClientPoliciesLoading || isDefaultPoliciesLoading,
    [isClientPoliciesLoading, isDefaultPoliciesLoading],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : policySummaryList || EMPTY_ARRAY,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Stack gap="24">
      <Table
        id={'leave-setting-summary'}
        table={table}
        loading={isLoading}
        showHeader={false}
        errorStateProps={{
          id: 'leave-setting-summary-error',
          title: ERROR_STATE_TITLE,
          description: ERROR_STATE_DESCRIPTION,
          callToAction: <Button onClick={window.location.reload}>Retry</Button>,
        }}
      />
      {statutoryCountryPolicy?.clientPolicyExplanations && (
        <Card p="24">
          <Stack gap="8">
            {statutoryCountryPolicy?.clientPolicyExplanations?.map(
              (explanation, explanationIdx) => (
                <Stack gap="0" key={explanationIdx}>
                  <Typography as="h3" size="14" weight="medium">
                    {explanation.title}
                  </Typography>
                  <Stack gap="4">
                    {explanation.descriptionItems?.map(
                      (descriptionItem, descriptionIdx) =>
                        Array.isArray(descriptionItem) ? (
                          <List
                            showDisc
                            gap="0"
                            items={descriptionItem.map((item, index) => ({
                              id: index,
                              name: item,
                            }))}
                            key={descriptionIdx}
                            keyExtractor="id"
                            render={(item) => (
                              <Typography as="span" size="14" color="subtle">
                                {item.name}
                              </Typography>
                            )}
                          />
                        ) : (
                          <Typography
                            as="span"
                            size="14"
                            color="subtle"
                            key={descriptionIdx}
                          >
                            {descriptionItem}
                          </Typography>
                        ),
                    )}
                  </Stack>
                </Stack>
              ),
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
